import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { farmAPI } from '../services/api';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const FarmContext = createContext();

const farmEnabled = (farm) => farm && farm.isActive !== false;

const pickCurrentFarm = (farms, preferred) => {
  if (preferred && farmEnabled(preferred)) {
    const fresh = farms.find((f) => f.id === preferred.id && farmEnabled(f));
    if (fresh) return fresh;
  }
  return farms.find(farmEnabled) || null;
};

const mergeFarm = (existing, incoming) => ({
  ...(existing || {}),
  ...(incoming || {}),
  currentUserRole: incoming?.currentUserRole || existing?.currentUserRole
});

// Cache keys for localStorage
const CACHE_KEYS = {
  FARMS: 'omabra_farms_cache',
  CURRENT_FARM: 'omabra_current_farm',
  CACHE_TIME: 'omabra_farms_cache_time'
};

// Cache duration: 5 minutes (data older than this shows loading indicator)
const CACHE_MAX_AGE = 5 * 60 * 1000;

// Helper to safely get cached data
const getCachedFarms = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEYS.FARMS);
    return cached ? JSON.parse(cached) : [];
  } catch {
    return [];
  }
};

const getCachedCurrentFarm = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEYS.CURRENT_FARM);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

const isCacheFresh = () => {
  try {
    const cacheTime = localStorage.getItem(CACHE_KEYS.CACHE_TIME);
    if (!cacheTime) return false;
    return Date.now() - parseInt(cacheTime, 10) < CACHE_MAX_AGE;
  } catch {
    return false;
  }
};

// Initialize with cached data for instant display
const cachedFarms = getCachedFarms();
const cachedCurrentFarm = getCachedCurrentFarm();
const hasFreshCache = isCacheFresh();

const initialState = {
  farms: cachedFarms,
  currentFarm: cachedCurrentFarm,
  loading: cachedFarms.length === 0, // Only show loading if no cached data
  refreshing: false, // Background refresh indicator
  error: null,
};

const farmReducer = (state, action) => {
  switch (action.type) {
    case 'FARM_START':
      return {
        ...state,
        // Only show full loading if no farms cached
        loading: state.farms.length === 0,
        refreshing: state.farms.length > 0, // Background refresh if we have cached data
        error: null,
      };
    case 'FARM_SUCCESS':
      return {
        ...state,
        farms: action.payload,
        loading: false,
        refreshing: false,
        error: null,
      };
    case 'FARM_FAILURE':
      return {
        ...state,
        loading: false,
        refreshing: false,
        error: action.payload,
      };
    case 'SET_CURRENT_FARM':
      return {
        ...state,
        currentFarm: action.payload,
      };
    case 'ADD_FARM':
      return {
        ...state,
        farms: [...state.farms, action.payload],
      };
    case 'UPDATE_FARM': {
      const existing = state.farms.find((farm) => farm.id === action.payload.id);
      const merged = mergeFarm(existing, action.payload);
      const farms = state.farms.map((farm) => (farm.id === merged.id ? merged : farm));
      const preferred = state.currentFarm?.id === merged.id ? merged : state.currentFarm;
      return {
        ...state,
        farms,
        currentFarm: pickCurrentFarm(farms, preferred),
        loading: false,
        refreshing: false
      };
    }
    case 'DELETE_FARM': {
      const farms = state.farms.filter((farm) => farm.id !== action.payload);
      return {
        ...state,
        farms,
        currentFarm: pickCurrentFarm(farms, state.currentFarm?.id === action.payload ? null : state.currentFarm),
        loading: false,
        refreshing: false
      };
    }
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

export const FarmProvider = ({ children }) => {
  const [state, dispatch] = useReducer(farmReducer, initialState);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      loadFarms();
    }
  }, [user]);

  useEffect(() => {
    try {
      if (state.currentFarm && farmEnabled(state.currentFarm)) {
        localStorage.setItem(CACHE_KEYS.CURRENT_FARM, JSON.stringify(state.currentFarm));
      } else {
        localStorage.removeItem(CACHE_KEYS.CURRENT_FARM);
      }
    } catch (e) {}
  }, [state.currentFarm]);

  const loadFarms = async (showToastOnError = true) => {
    try {
      dispatch({ type: 'FARM_START' });
      const response = await farmAPI.getFarms();
      const farms = response.data.farms || [];
      dispatch({ type: 'FARM_SUCCESS', payload: farms });
      
      // Cache farms to localStorage for instant display on next visit
      try {
        localStorage.setItem(CACHE_KEYS.FARMS, JSON.stringify(farms));
        localStorage.setItem(CACHE_KEYS.CACHE_TIME, Date.now().toString());
      } catch (e) {
        console.warn('Failed to cache farms:', e);
      }
      
      const nextCurrent = pickCurrentFarm(farms, state.currentFarm);
      dispatch({ type: 'SET_CURRENT_FARM', payload: nextCurrent });
      try {
        if (nextCurrent) localStorage.setItem(CACHE_KEYS.CURRENT_FARM, JSON.stringify(nextCurrent));
        else localStorage.removeItem(CACHE_KEYS.CURRENT_FARM);
      } catch (e) {}
    } catch (error) {
      // Don't show 402 errors as toast (handled by subscription banner)
      if (error.response?.status === 402) {
        dispatch({ type: 'FARM_FAILURE', payload: 'Subscription required' });
        return;
      }
      const errorMessage = error.response?.data?.message || 'Failed to load farms';
      dispatch({ type: 'FARM_FAILURE', payload: errorMessage });
      if (showToastOnError) toast.error(errorMessage);
    }
  };

  const getFarm = async (farmId) => {
    try {
      // Optimistic: if we already have this farm in memory, set it immediately
      const local = state.farms.find(f => f.id === farmId);
      if (local) {
        dispatch({ type: 'SET_CURRENT_FARM', payload: local });
      } else {
        dispatch({ type: 'FARM_START' });
      }
      const response = await farmAPI.getFarm(farmId);
      const farm = response.data.farm;
      // Preserve currentUserRole from local membership if backend doesn't include it
      const merged = local ? { ...farm, currentUserRole: local.currentUserRole } : farm;
      dispatch({ type: 'SET_CURRENT_FARM', payload: merged });
      return { success: true, farm };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to load farm';
      dispatch({ type: 'FARM_FAILURE', payload: errorMessage });
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const createFarm = async (farmData) => {
    try {
      dispatch({ type: 'FARM_START' });
      const response = await farmAPI.createFarm(farmData);
      const farm = response.data.farm;
      dispatch({ type: 'ADD_FARM', payload: farm });
      toast.success('Farm created successfully!');
      return { success: true, farm };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create farm';
      const errorDetails = error.response?.data?.details || '';
      const fullErrorMessage = errorDetails ? `${errorMessage}: ${errorDetails}` : errorMessage;
      
      console.error('Farm creation error:', error.response?.data || error);
      dispatch({ type: 'FARM_FAILURE', payload: fullErrorMessage });
      toast.error(fullErrorMessage);
      return { success: false, error: fullErrorMessage };
    }
  };

  const updateFarm = async (farmId, farmData) => {
    try {
      dispatch({ type: 'FARM_START' });
      const response = await farmAPI.updateFarm(farmId, farmData);
      const farm = response.data.farm;
      dispatch({ type: 'UPDATE_FARM', payload: farm });
      toast.success('Farm updated successfully!');
      return { success: true, farm };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update farm';
      dispatch({ type: 'FARM_FAILURE', payload: errorMessage });
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const setFarmStatus = async (farmId, isActive) => {
    try {
      const response = await farmAPI.setFarmStatus(farmId, isActive);
      const farm = response.data.farm;
      dispatch({ type: 'UPDATE_FARM', payload: farm });
      toast.success(isActive ? 'Farm enabled' : 'Farm disabled — data is kept');
      return { success: true, farm };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update farm status';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const deleteFarm = async (farmId) => {
    try {
      dispatch({ type: 'FARM_START' });
      await farmAPI.deleteFarm(farmId);
      dispatch({ type: 'DELETE_FARM', payload: farmId });
      
      // Update cache after deletion
      try {
        const updatedFarms = state.farms.filter(f => f.id !== farmId);
        localStorage.setItem(CACHE_KEYS.FARMS, JSON.stringify(updatedFarms));
        if (state.currentFarm?.id === farmId) {
          localStorage.removeItem(CACHE_KEYS.CURRENT_FARM);
        }
      } catch (e) {}
      
      toast.success('Farm deleted successfully!');
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete farm';
      dispatch({ type: 'FARM_FAILURE', payload: errorMessage });
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };
  
  // Clear cache (call on logout)
  const clearCache = () => {
    try {
      localStorage.removeItem(CACHE_KEYS.FARMS);
      localStorage.removeItem(CACHE_KEYS.CURRENT_FARM);
      localStorage.removeItem(CACHE_KEYS.CACHE_TIME);
    } catch (e) {}
  };

  const setCurrentFarm = (farm) => {
    const next = farmEnabled(farm) ? farm : pickCurrentFarm(state.farms, null);
    dispatch({ type: 'SET_CURRENT_FARM', payload: next });
    try {
      if (next) {
        localStorage.setItem(CACHE_KEYS.CURRENT_FARM, JSON.stringify(next));
      } else {
        localStorage.removeItem(CACHE_KEYS.CURRENT_FARM);
      }
    } catch (e) {}
  };

  const selectFarmById = (farmId) => {
    const found = state.farms.find((f) => f.id === farmId && farmEnabled(f)) || null;
    setCurrentFarm(found);
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value = {
    ...state,
    activeFarms: state.farms.filter(farmEnabled),
    loadFarms,
    getFarm,
    createFarm,
    updateFarm,
    setFarmStatus,
    deleteFarm,
    setCurrentFarm,
    selectFarmById,
    clearError,
    clearCache,
  };

  return (
    <FarmContext.Provider value={value}>
      {children}
    </FarmContext.Provider>
  );
};

export const useFarm = () => {
  const context = useContext(FarmContext);
  if (!context) {
    throw new Error('useFarm must be used within a FarmProvider');
  }
  return context;
};
